import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Save, X } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { RateCardPicker, rateCardById, loadRateCards } from '@/lib/rateCards';
import { GoldRatePicker, currentGold, purityPercent, setGoldTyped } from '@/lib/goldRate';
import { tagMaterials } from '@/components/jewellery/JewelleryTagViewer';

// Price output for tags / Excel (Tag Domain): mode 'none' | 'total' | 'lines', chosen rate card, user rights.
let state = { mode: 'none', cardId: '', canView: false, canEdit: false, ask: null, stoneRates: {} };
const subs = new Set();
const set = (p) => { state = { ...state, ...p }; subs.forEach((f) => f()); };
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const usePricing = () => useSyncExternalStore(subscribe, () => state);
export const getPricing = () => state;
export const setPricing = (p) => set(p);
export function setRatePerms(view, edit) { set({ canView: !!view, canEdit: !!edit, ...(view ? {} : { mode: 'none', cardId: '' }) }); }
export const pricingOn = () => state.canView && state.mode !== 'none';

export const inr = (n) => Math.round(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
export const rateLabel = (m) => { const c = String(m.classification || '').trim(); const n = String(m.note || '').trim(); return n ? `${c} - ${n}` : c; };
const purityOf = (data) => (data.purityText && data.purityText !== '—' ? data.purityText : '');

// Gold rate per g = 100% gold rate (typed / portal) × the piece's purity %.
export const purityPctOf = (data) => (data.purityMissing ? null : data.purityPct || purityPercent(purityOf(data)));
export function goldRateFor(data, gold = currentGold()) {
  const pct = purityPctOf(data);
  return gold && pct ? Math.round((gold.rate * pct) / 100) : null;
}

// Colour stones are priced per piece (quality varies); diamonds / pearls / others come from the rate card.
export const isStone = (m) => m.materialType === 'color_stone';
export const stoneKey = (data, m) => `${data.id}|${rateLabel(m)}`;
export const addStoneRates = (r) => set({ stoneRates: { ...state.stoneRates, ...(r || {}) } });
// Opening a list: only that list's own per-piece stone rates apply.
export const applyListStones = (r) => set({ stoneRates: { ...(r || {}) } });
// Remember per-piece colour stone rates on a saved list (merged / unsaved lists keep them for the session).
export async function persistListStones(l, sr) {
  if (!l?.id || l.id === 'merged') return;
  const stone_rates = { ...(l.stone_rates || {}), ...sr };
  await notesApi.put(`/notes/tag-workspace/tag-lists/${l.id}`, { name: l.name, items: l.items, rm_mode: l.rm_mode, show_pieces: l.show_pieces, rate_card_id: l.rate_card_id || '', sales_ids: l.sales_ids || [], stone_rates });
  l.stone_rates = stone_rates;
}
export const stonesFor = (ids) => Object.fromEntries(Object.entries(state.stoneRates).filter(([k]) => ids.includes(k.split('|')[0])));
export function lineRate(data, m, card, stoneRates = state.stoneRates) {
  const own = isStone(m) ? stoneRates[stoneKey(data, m)] : undefined;
  // QR viewers have no per-piece stone rates, so stones fall back to the rate card there.
  const r = isStone(m) && !(state.stoneFromCard && own == null) ? own : (card?.lines || {})[rateLabel(m)];
  return r == null || r === '' ? null : Math.round(Number(r));
}

// Amount per displayed line: expanded = ct × rate; merged / raw lines = sum of their expanded source amounts.
export function priceTag(data, rmMode, card, materials) {
  const srcLine = new Map(); // source index → expanded line
  const expRate = new Map();
  tagMaterials(data, 'priced').forEach((m) => {
    const r = lineRate(data, m, card);
    expRate.set(m.key, r);
    const e = { label: rateLabel(m), src: m.src || [], rate: r == null ? null : Math.round(r), amount: r == null ? null : Math.round(Number(m.carats || 0) * Math.round(r)) };
    e.src.forEach((i) => srcLine.set(i, e));
  });
  // Merged line = whole expanded lines' rounded amounts (+ ct × rate for partly included lines).
  const mergedAmount = (src) => {
    const set = new Set(src);
    let t = 0;
    for (const e of new Set(src.map((i) => srcLine.get(i)).filter(Boolean))) {
      if (e.rate == null) return null;
      t += e.src.every((i) => set.has(i)) ? e.amount : e.src.filter((i) => set.has(i)).reduce((a, i) => a + Number(data.materials[i]?.carats || 0) * e.rate, 0);
    }
    return Math.round(t);
  };
  const view = materials || tagMaterials(data, rmMode);
  const byKey = {};
  view.forEach((m) => {
    const one = new Set((m.src || []).map((i) => srcLine.get(i)));
    if (rmMode === 'expanded' && one.size <= 1 && !(m.parts > 1)) {
      const e = [...one][0];
      const r = expRate.has(m.key) ? expRate.get(m.key) : e ? e.rate : lineRate(data, m, card);
      byKey[m.key] = { rate: r, amount: r == null ? null : Math.round(Number(m.carats || 0) * r) };
      return;
    }
    const amount = mergedAmount(m.src || []);
    byKey[m.key] = { rate: amount != null && Number(m.carats) > 0 ? Math.round(amount / Number(m.carats)) : null, amount };
  });
  const net = Number(Number(data.net || 0).toFixed(3));
  const goldRate = net > 0 && purityPctOf(data) ? goldRateFor(data) : 0;
  const goldAmt = goldRate == null ? null : Math.round(net * goldRate);
  const labourRate = card?.labour == null ? null : Math.round(card.labour);
  const labourAmt = labourRate == null ? null : Math.round(net * labourRate);
  // Extra charges (Pohai, Hallmarking …) are added to the value; never printed on the tag.
  const charges = (data.charges || []).map((c) => ({ name: c.name, amount: c.amount == null || c.amount === '' ? null : Math.round(Number(c.amount)) }));
  const all = [...Object.values(byKey).map((x) => x.amount), goldAmt, labourAmt, ...charges.map((c) => c.amount)];
  const total = all.some((x) => x == null) ? null : all.reduce((t, x) => t + x, 0);
  // Labels with no price configured, in tag order (the total is withheld while any are missing).
  const noPurity = net > 0 && !purityPctOf(data);
  const missing = [...(goldAmt == null ? ['Gold rate'] : []), ...(noPurity ? ['Gold purity'] : []), ...view.filter((m) => byKey[m.key]?.amount == null).flatMap((m) => { const parts = [...new Set((m.src || []).map((i) => srcLine.get(i)).filter((e) => e && e.rate == null))]; return parts.length ? parts.map((e) => e.label) : [rateLabel(m)]; }), ...(labourAmt == null ? ['Labour'] : []), ...charges.filter((c) => c.amount == null).map((c) => `${c.name} (extra charge)`)];
  return { byKey, goldRate, goldAmt, labourRate, labourAmt, charges, total, missing: [...new Set(missing)] };
}

// Tag lines with amounts (and Gold / Labour / Total rows) for the chosen price mode.
export function pricedMaterials(data, rmMode, materials) {
  if (!pricingOn() || !data) return materials;
  const card = rateCardById(state.cardId);
  if (!card) return materials;
  const p = priceTag(data, rmMode, card, materials);
  const total = p.missing.length
    ? { key: '__missing', priceRow: 'missing', missing: p.missing }
    : { key: '__total', priceRow: 'total', classification: 'Total value', amount: p.total };
  if (state.mode === 'total') return [...materials, total];
  // Strict order: Net weight (gold) → raw materials → Labour → total.
  return [
    { key: '__gold', priceRow: 'line', classification: `Net wt ${Number(data.net || 0).toFixed(3)} g (gold)`, amount: p.goldAmt },
    ...materials.map((m) => ({ ...m, amount: p.byKey[m.key]?.amount })),
    { key: '__labour', priceRow: 'line', classification: 'Labour', amount: p.labourAmt },
    total,
  ];
}

// Missing rates, in asking order: diamonds (card), colour stones (per piece), pearls + others (card), labour, gold.
export function missingRates(datas, card, gold = currentGold()) {
  const dia = new Set(); const pearl = new Set(); const other = new Set();
  const stones = [];
  let needGold = false;
  datas.forEach((d) => {
    const mine = [];
    tagMaterials(d, 'priced').forEach((m) => {
      // Colour stones are always confirmed piece by piece, even when a rate exists.
      if (isStone(m)) { mine.push({ key: stoneKey(d, m), label: rateLabel(m), carats: Number(m.carats || 0), pieces: m.pieces }); return; }
      if (lineRate(d, m, card) != null) return;
      (m.materialType === 'diamond' ? dia : m.materialType === 'pearl' ? pearl : other).add(rateLabel(m));
    });
    if (mine.length) stones.push({ id: d.id, jno: d.jewelleryNo, image: d.image, lines: mine });
    if (Number(d.net) > 0 && purityPctOf(d) && goldRateFor(d, gold) == null) needGold = true;
  });
  const sort = (x) => [...x].sort();
  // Pieces with extra charges are always confirmed (pre-filled, editable, saved back to the piece).
  const charges = datas.filter((d) => d.charges?.length).map((d) => ({ id: d.id, jno: d.jewelleryNo, image: d.image, data: d, lines: d.charges.map((c, i) => ({ key: `${d.id}|${i}`, name: c.name, amount: c.amount })) }));
  return { diamonds: sort(dia), stones, pearls: sort(pearl), others: sort(other), gold: needGold, labour: card?.labour == null, charges };
}
const hasMissing = (m) => m.diamonds.length || m.stones.length || m.pearls.length || m.others.length || m.gold || m.labour || m.charges.length;

// Gate before any priced output; resolves true when every rate is present (after the user fills gaps).
export async function ensureRates(datas, where = 'page', onStones) {
  if (!pricingOn()) return true;
  const card = rateCardById(state.cardId);
  if (!card) { toast.error('Choose a rate card for prices, or switch prices off.'); return false; }
  const noPurity = datas.filter((d) => Number(d.net) > 0 && !purityPctOf(d)).map((d) => d.jewelleryNo);
  if (noPurity.length) toast.warning(`No gold purity on ${noPurity.join(', ')} — gold amount left out for ${noPurity.length === 1 ? 'it' : 'them'}.`);
  const miss = missingRates(datas, card);
  if (!hasMissing(miss)) return true;
  return new Promise((resolve) => set({ ask: { card, miss, resolve, where, onStones } }));
}

const INPUT = 'h-8 w-28 rounded-md border border-slate-200 px-2 text-sm';
function Section({ title, hint, testid, children }) {
  return (
    <div className="space-y-1 pb-2" data-testid={testid}>
      <div className="sticky top-0 z-[1] flex items-baseline justify-between bg-white py-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-700">{title}</span>{hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}</div>
      {children}
    </div>
  );
}
const MISS_ROW = 'flex items-center gap-2 rounded-md bg-rose-50 px-2 py-1';

// Dialog listing rates missing in the card; filling all of them saves the card and continues.
export function MissingRatesHost({ where = 'page', contained = false }) {
  const { ask, canEdit, stoneRates: stoneRatesNow } = usePricing();
  useEffect(() => {
    if (!ask) return undefined;
    const esc = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); e.preventDefault(); ask.resolve(false); set({ ask: null }); } };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [ask]);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const shown = !!ask && ask.where === where;
  useEffect(() => {
    // Inside a scrolling dialog: pin to its visible area while open.
    const host = contained && shown ? boxRef.current?.parentElement : null;
    if (!host) return undefined;
    const prev = host.style.overflowY;
    host.scrollTop = 0; host.style.overflowY = 'hidden';
    return () => { host.style.overflowY = prev; };
  }, [contained, shown]);
  if (!shown) return null;
  const { card, miss } = ask;
  const cardLines = [...miss.diamonds, ...miss.pearls, ...miss.others];
  const stoneKeys = miss.stones.flatMap((p) => p.lines.map((l) => `stone:${l.key}`));
  const chargeKeys = miss.charges.flatMap((p) => p.lines.map((l) => `charge:${l.key}`));
  const keys = [...cardLines.map((k) => `lines:${k}`), ...stoneKeys, ...chargeKeys, ...(miss.gold ? ['gold'] : []), ...(miss.labour ? ['labour'] : [])];
  const cardKeys = keys.filter((k) => k.startsWith('lines:') || k === 'labour');
  const onlyStones = !cardKeys.length && !miss.gold;
  const confirmTitle = stoneKeys.length && chargeKeys.length ? 'Confirm colour stone rates & extra charges' : chargeKeys.length ? 'Confirm extra charges' : 'Confirm colour stone rates';
  const close = (ok) => { ask.resolve(ok); setVals({}); set({ ask: null }); };
  const save = async () => {
    keys.forEach((k) => { if (vals[k] === undefined && dflt(k) !== '') vals[k] = dflt(k); });
    if (keys.some((k) => vals[k] === undefined || vals[k] === '' || Number(vals[k]) < 0)) { toast.error('Fill in every rate and extra charge (0 is allowed) to continue.'); return; }
    const pick = (b) => Object.fromEntries(keys.filter((k) => k.startsWith(`${b}:`)).map((k) => [k.slice(b.length + 1), Math.round(Number(vals[k]))]));
    const body = { name: card.name, lines: { ...card.lines, ...pick('lines') }, gold: card.gold || {}, labour: miss.labour ? Math.round(Number(vals.labour)) : card.labour };
    setBusy(true);
    try {
      if (miss.gold) setGoldTyped(String(Math.round(Number(vals.gold))));
      if (stoneKeys.length) {
        const sr = Object.fromEntries(stoneKeys.map((k) => [k.slice(6), Math.round(Number(vals[k]))]));
        addStoneRates(sr);
        await ask.onStones?.(sr);
      }
      if (chargeKeys.length) {
        const items = miss.charges.map((p) => ({ id: p.id, charges: p.lines.map((l) => ({ name: l.name, amount: Math.round(Number(vals[`charge:${l.key}`])) })) }));
        await notesApi.put('/notes/tag-workspace/jewellery-charges', { items });
        // Same objects feed the Excel / PDF, so they pick up the confirmed amounts.
        miss.charges.forEach((p, i) => { p.data.charges = items[i].charges; });
        ask.onCharges?.(items);
      }
      if (cardKeys.length) { await notesApi.put(`/notes/tag-workspace/rate-cards/${card.id}`, body); await loadRateCards(); toast.success(`Rates saved to "${card.name}".`); }
      close(true);
    }
    catch (e) { toast.error(extractError(e, 'Could not save the rates.')); } finally { setBusy(false); }
  };
  // Stone boxes: rate last entered for this piece, else the rate card's rate.
  const dflt = (k) => {
    if (k.startsWith('charge:')) {
      const l = miss.charges.flatMap((p) => p.lines).find((x) => `charge:${x.key}` === k);
      return l?.amount == null ? '' : String(Math.round(Number(l.amount)));
    }
    if (!k.startsWith('stone:')) return '';
    const own = stoneRatesNow[k.slice(6)];
    const fromCard = card?.lines?.[k.split('|').slice(1).join('|')];
    const r = own ?? fromCard;
    return r == null || r === '' ? '' : String(Math.round(Number(r)));
  };
  const row = (k, label) => (
    <div key={k} className={MISS_ROW} data-testid="missing-rate-row">
      <span className="min-w-0 flex-1 truncate font-mono text-xs" title={label}>{label}</span>
      <input type="number" min="0" step="1" disabled={!canEdit && (k.startsWith('lines:') || k === 'labour')} value={vals[k] ?? dflt(k)} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} placeholder={k.startsWith('charge:') ? 'Amount' : 'Rate'} className={INPUT} data-testid="missing-rate-input" />
    </div>
  );
  return (
    <div ref={boxRef} className={`${contained ? 'absolute' : 'fixed'} inset-0 z-[400] flex items-center justify-center p-3`} style={{ pointerEvents: 'auto' }} data-testid="missing-rates-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => close(false)} />
      <div className="relative z-10 flex max-h-full w-full max-w-lg flex-col rounded-2xl bg-white p-4 text-slate-800 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold"><AlertTriangle size={17} className="text-rose-600" /> {onlyStones ? confirmTitle : `Rates missing in “${card.name}”`}</h3>
          <button onClick={() => close(false)} aria-label="Close" data-testid="missing-rates-close"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500">{onlyStones ? 'Confirm the amounts for each piece (edit if needed).' : 'Prices can only be shown when every line has a rate.'} {onlyStones ? '' : canEdit ? 'Fill these in (whole rupees). Diamond, pearl and labour rates are saved to the rate card; colour stones are priced per piece.' : 'You can only view rate cards — ask someone with rights to change rate cards to add raw-material / labour rates, or switch prices off.'}</p>
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto" data-testid="missing-rates-list">
          {miss.diamonds.length ? <Section title="Diamonds" hint="General rate — saved to the rate card" testid="missing-rates-diamonds">{miss.diamonds.map((l) => row(`lines:${l}`, `${l} — per ct`))}</Section> : null}
          {miss.stones.length ? (
            <Section title="Colour stones" hint={ask.onStones ? "Confirm or edit each piece's rate — remembered for this list" : "Confirm or edit each piece's rate"} testid="missing-rates-stones">
              {miss.stones.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 p-2" data-testid="missing-rates-stone-piece">
                  <div className="mb-1.5 flex items-center gap-2">
                    {p.image ? <img src={p.image} alt="" className="h-14 w-14 shrink-0 rounded-md border border-slate-200 object-cover" data-testid="missing-rates-stone-thumb" /> : <span className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-slate-100 text-[10px] text-slate-400">No photo</span>}
                    <span className="font-mono text-sm font-semibold">{p.jno}</span>
                  </div>
                  <div className="space-y-1">{p.lines.map((l) => row(`stone:${l.key}`, `${l.label} · ${l.carats.toFixed(2)} ct — per ct`))}</div>
                </div>
              ))}
            </Section>
          ) : null}
          {miss.charges.length ? (
            <Section title="Extra charges" hint="Added to the item value — saved to the piece" testid="missing-rates-charges">
              {miss.charges.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 p-2" data-testid="missing-rates-charge-piece">
                  <div className="mb-1.5 flex items-center gap-2">
                    {p.image ? <img src={p.image} alt="" className="h-10 w-10 shrink-0 rounded-md border border-slate-200 object-cover" /> : null}
                    <span className="font-mono text-sm font-semibold">{p.jno}</span>
                  </div>
                  <div className="space-y-1">{p.lines.map((l) => row(`charge:${l.key}`, `${l.name} — ₹ amount`))}</div>
                </div>
              ))}
            </Section>
          ) : null}
          {miss.pearls.length ? <Section title="Pearls" hint="General rate — saved to the rate card" testid="missing-rates-pearls">{miss.pearls.map((l) => row(`lines:${l}`, `${l} — per ct`))}</Section> : null}
          {miss.others.length ? <Section title="Other materials" hint="Saved to the rate card" testid="missing-rates-others">{miss.others.map((l) => row(`lines:${l}`, `${l} — per ct`))}</Section> : null}
          {miss.labour || miss.gold ? (
            <Section title="Labour & gold" testid="missing-rates-labour-gold">
              {miss.labour ? row('labour', 'Labour — rate per g of net wt') : null}
              {miss.gold ? row('gold', 'Gold 100% — rate per g (used for this output only, converted to each purity)') : null}
            </Section>
          ) : null}
        </div>
        <div className="mt-3 flex shrink-0 justify-end gap-2 border-t border-slate-100 pt-3">
          <button onClick={() => close(false)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm" data-testid="missing-rates-cancel">Cancel</button>
          {canEdit || !cardKeys.length ? <button onClick={save} disabled={busy} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="missing-rates-save"><Save size={14} /> {onlyStones ? 'Confirm rates & continue' : 'Save rates & continue'}</button> : null}
        </div>
      </div>
    </div>
  );
}

// Prices on / off + rate card + gold rate, shown together wherever tags or Excel are generated.
export function PricingControls({ testid = 'pricing', up = false, portal = false, cardId, onCard }) {
  const s = usePricing();
  if (!s.canView) return null;
  const card = cardId !== undefined ? cardId : s.cardId;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testid}-controls`}>
      <select value={s.mode} onChange={(e) => set({ mode: e.target.value })} title="Prices on tags and Excel" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700" data-testid={`${testid}-mode`}>
        <option value="none">Without prices</option>
        <option value="total">Prices: total value</option>
        <option value="lines">Prices: amount per line</option>
      </select>
      {s.mode !== 'none' ? <RateCardPicker value={card} onChange={(v) => (onCard ? onCard(v) : set({ cardId: v }))} testid={`${testid}-rate-card`} /> : null}
      {s.mode !== 'none' ? <GoldRatePicker up={up} portal={portal} testid={`${testid}-gold-rate`} /> : null}
    </div>
  );
}
